<template>
  <div class="pa-4">
    <!-- Header -->
    <v-card class="mb-8" elevation="0">
      <v-card-text class="text-center py-8">
        <h1 class="text-h3 font-weight-bold mb-2">Data Availability Dashboard</h1>
        <p class="text-subtitle-1 text--secondary">Vue complète de la disponibilité des données</p>
      </v-card-text>
    </v-card>

    <!-- Filters Section -->
    <filters />

    <!-- Partners Overview Section -->
    <PartnersOverviewCard 
      :data="partnersOverview" 
      :loading="loading.partnersOverview"
      :error="errors.partnersOverview"
    />

    <!-- Audience Overview Section -->
    <AudienceOverviewCard 
      :data="audienceOverview" 
      :loading="loading.audienceOverview"
      :error="errors.audienceOverview"
    />

    <!-- Editorial Overview Section -->
    <EditorialOverviewCard 
      :data="editorialOverview" 
      :loading="loading.editorialOverview"
      :error="errors.editorialOverview"
    />

  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { cacheTime } from "@axios";
import useAxios from "@/hooks/useAxios";
import { runParallelAsyncs } from "@/utils/async";

import PartnersOverviewCard from "@/components/admin/data-availability/cards/PartnersOverviewCard.vue";
import AudienceOverviewCard from "@/components/admin/data-availability/cards/AudienceOverviewCard.vue";
import EditorialOverviewCard from "@/components/admin/data-availability/cards/EditorialOverviewCard.vue";
import Filters from "@/components/admin/data-availability/filters/Filters.vue";

export default {
  name: "DataAvailability",
  components: {
    PartnersOverviewCard,
    AudienceOverviewCard,
    EditorialOverviewCard,
    Filters
  },
  data() {
    return {
      lastUpdate: new Date().toLocaleString('fr-FR'),
      
      // Loading states
      loading: {
        partnersOverview: false,
        audienceOverview: false,
        editorialOverview: false
      },
      
      // Error states
      errors: {
        partnersOverview: null,
        audienceOverview: null,
        editorialOverview: null
      },
      
      // Partners Overview data
      partnersOverview: {
        dataUploaded: {
          lines: 1235,
          trend: 10
        },
        revenue: {
          amount: 540000,
          trend: 5
        },
        activePartners: 76,
        dataPartnersIssues: {
          dataDrop: []
        },
        revenueDrops: [
          { name: 'Digiteka', data: [] },
          { name: 'Display Prebid', data: [] }
        ]
      },
      
      // Audience Overview data
      audienceOverview: {
        stats: {
          views: {
            value: 1235,
            trend: 10,
            trendDirection: "up"
          },
          sessions: {
            value: 3000000,
            trend: 7,
            trendDirection: "up"
          }
        },
        audienceIssues: {
          gaDataDrop: [],
          wysistatDataDrop: []
        }
      },
      
      // Editorial Overview data
      editorialOverview: {
        stats: {
          articlesCreated: {
            value: 0,
            trend: null,
            trendDirection: null
          }
        },
        editorialIssues: {
          postCreationDrop: []
        }
      }
    };
  },
  
  setup() {
    const { axiosGet } = useAxios();
    return { axiosGet };
  },
  
  computed: {
    ...mapGetters('dataAvailability', [
      'getPartnerViewGroups',
      'getPartnerViews',
      'getSiteGroups',
      'getSites',
      'getThreshold'
    ]),
    ...mapGetters('common', ['getApplyFilters']),
    
    partnerViewGroups() {
      return this.$store.getters['dataAvailability/getPartnerViewGroups'];
    },
    partnerViews() {
      return this.$store.getters['dataAvailability/getPartnerViews'];
    },
    siteGroups() {
      return this.$store.getters['dataAvailability/getSiteGroups'];
    },
    sites() {
      return this.$store.getters['dataAvailability/getSites'];
    },
    threshold() {
      return this.$store.getters['dataAvailability/getThreshold'];
    },
    applyFiltersFlag() {
      return this.getApplyFilters;
    }
  },
  
  async mounted() {
    await this.setDefaultFilters();
    // Fetch data after setting default filters
    await this.fetchDataAvailability();
  },
  
  watch: {
    applyFiltersFlag() {
      if (this.applyFiltersFlag) {
        this.fetchDataAvailability();
      }
    }
  },
  
  methods: {
    async setDefaultFilters() {
      // Check if filters are already in URL query params
      const query = this.$route.query;
      
      if (!query.partnerViewGroups && !query.partnerViews && !query.siteGroups) {
        // Load partner view groups from API
        const partnerViewGroups = await this.$store.dispatch('common/getPartnerViewGroupList');
        
        // Set default partner view group (Export Suivi Général)
        const exportSuiviGeneral = partnerViewGroups.find(
          (g) => g.name === "Export Suivi Général"
        );
        
        if (exportSuiviGeneral) {
          await this.$store.dispatch('dataAvailability/updatePartnerViewGroups', [exportSuiviGeneral.id]);
        }
        
        // Set default site groups (IDs 4 and 12)
        await this.$store.dispatch('dataAvailability/updateSiteGroups', [4, 12]);
      } else {
        // Load filters from URL
        if (query.partnerViewGroups) {
          await this.$store.dispatch('dataAvailability/updatePartnerViewGroups', query.partnerViewGroups.split(',').map(Number));
        }
        if (query.partnerViews) {
          await this.$store.dispatch('dataAvailability/updatePartnerViews', query.partnerViews.split(',').map(Number));
        }
        if (query.siteGroups) {
          await this.$store.dispatch('dataAvailability/updateSiteGroups', query.siteGroups.split(',').map(Number));
        }
        if (query.sites) {
          await this.$store.dispatch('dataAvailability/updateSites', query.sites.split(',').map(Number));
        }
        if (query.threshold) {
          await this.$store.dispatch('dataAvailability/updateThreshold', parseInt(query.threshold));
        }
      }
    },
    
    
    async fetchDataAvailability() {
      // Build query params
      const queryParams = {
        ...(this.sites && this.sites.length > 0
          ? { sites: this.sites.join(",") }
          : {}),
        ...(this.siteGroups && this.siteGroups.length > 0
          ? { site_groups: this.siteGroups.join(",") }
          : {}),
        ...(this.partnerViews && this.partnerViews.length > 0
          ? { partner_views: this.partnerViews.join(",") }
          : {}),
        ...(this.partnerViewGroups && this.partnerViewGroups.length > 0
          ? { partner_view_groups: this.partnerViewGroups.join(",") }
          : {}),
        ...(this.threshold !== null && this.threshold !== undefined
          ? { threshold: this.threshold }
          : {}),
      };

      // Fetch data
      await Promise.all([
        this.fetchPartnersOverview(queryParams),
        this.fetchAudienceOverview(queryParams),
        this.fetchEditorialOverview(queryParams)
      ]);
      
      this.lastUpdate = new Date().toLocaleString('fr-FR');
      
      // Reset apply filters flag
      this.$store.dispatch('common/updateApplyFilters', false);
    },
    
    async fetchPartnersOverview(queryParams) {
      this.loading.partnersOverview = true;
      this.errors.partnersOverview = null;
      try {
        const response = await this.axiosGet(
          '/data-availability/partners-overview',
          queryParams,
          {
            cache: {
              maxAge: cacheTime,
            },
          }
        );
        console.log('Partners Overview API Response:', response.data);
        this.partnersOverview = response.data;
      } catch (error) {
        this.errors.partnersOverview = "Erreur lors du chargement de l'aperçu des partenaires";
        console.error('Error fetching partners overview:', error);
      } finally {
        this.loading.partnersOverview = false;
      }
    },
    
    async fetchAudienceOverview(queryParams) {
      this.loading.audienceOverview = true;
      this.errors.audienceOverview = null;
      try {
        const response = await this.axiosGet(
          '/data-availability/audience-overview',
          queryParams,
          {
            cache: {
              maxAge: cacheTime,
            },
          }
        );
        console.log('Audience Overview API Response:', response.data);
        this.audienceOverview = response.data;
      } catch (error) {
        this.errors.audienceOverview = "Erreur lors du chargement de l'aperçu de l'audience";
        console.error('Error fetching audience overview:', error);
      } finally {
        this.loading.audienceOverview = false;
      }
    },
    
    async fetchEditorialOverview(queryParams) {
      this.loading.editorialOverview = true;
      this.errors.editorialOverview = null;
      try {
        const response = await this.axiosGet(
          '/data-availability/editorial-overview',
          queryParams,
          {
            cache: {
              maxAge: cacheTime,
            },
          }
        );
        console.log('Editorial Overview API Response:', response.data);
        this.editorialOverview = response.data;
      } catch (error) {
        this.errors.editorialOverview = "Erreur lors du chargement de l'aperçu éditorial";
        console.error('Error fetching editorial overview:', error);
      } finally {
        this.loading.editorialOverview = false;
      }
    }
  }
};
</script>

<style scoped>
/* Main dashboard styles */
.v-chip {
  font-weight: 500;
}
</style>
